import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { getConfig, isAuthenticated, loadServerConfig, isDevelopment, companyAccessError } from '@/api/config'
import { companyPath, safeCompanyReturn } from '@/utils/companyNavigation'
import { useAuthStore } from '@/stores/auth'

const companyRoutes: RouteRecordRaw[] = [
  { path: '', name: 'company-home', component: () => import('@/views/LoginView.vue') },
  { path: 'login', name: 'login', component: () => import('@/views/LoginView.vue') },
    {
      path: 'dashboard',
      name: 'dashboard',
      component: () => import('@/views/DashboardView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: 'proofs',
      name: 'proofs',
      component: () => import('@/views/ProofsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: 'proofs/new-ad/:orderImageKey',
      name: 'new-ad-detail',
      component: () => import('@/views/NewAdDetailView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: 'proofs/:proofKey',
      name: 'proof-detail',
      component: () => import('@/views/ProofDetailView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: 'accounts',
      name: 'accounts',
      component: () => import('@/views/AccountsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: 'accounts/:advertiserKey/pay',
      name: 'account-payment',
      component: () => import('@/views/PaymentView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: 'accounts/:advertiserKey',
      name: 'account-detail',
      component: () => import('@/views/AccountDetailView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: 'orders/new/:advertiserKey?',
      name: 'new-order',
      component: () => import('@/views/NewOrderView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: 'deals',
      name: 'deals',
      component: () => import('@/views/DealsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: 'deals/new',
      name: 'deal-new',
      component: () => import('@/views/DealDetailView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: 'deals/:dealKey',
      name: 'deal-detail',
      component: () => import('@/views/DealDetailView.vue'),
      meta: { requiresAuth: true }
    },


]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }

    const isSamePathNavigation = to.path === from.path
    const isContactFocusNavigation = to.name === 'account-detail'
      && (to.query.focusContact === '1'
        || to.query.focusContactKey != null
        || to.query.openContact != null
        || to.query.contactName != null
        || to.query.matchedValue != null)

    if (isSamePathNavigation || isContactFocusNavigation) {
      return false
    }

    return { top: 0 }
  },
  routes: [
    { path: '/', name: 'welcome', component: () => import('@/views/NoTenantView.vue') },
    { path: '/no-tenant', name: 'no-tenant', component: () => import('@/views/NoTenantView.vue') },
    {
      path: '/admin',
      name: 'admin-login',
      component: () => import('@/views/admin/AdminLoginView.vue'),
      meta: { isAdmin: true }
    },
    {
      path: '/admin/tenants',
      name: 'admin-tenants',
      component: () => import('@/views/admin/AdminTenantsView.vue'),
      meta: { isAdmin: true, requiresAdminAuth: true }
    },
    {
      path: '/admin/tenants/new',
      name: 'admin-tenant-new',
      component: () => import('@/views/admin/AdminTenantEditView.vue'),
      meta: { isAdmin: true, requiresAdminAuth: true }
    },
    {
      path: '/admin/tenants/:id',
      name: 'admin-tenant-edit',
      component: () => import('@/views/admin/AdminTenantEditView.vue'),
      meta: { isAdmin: true, requiresAdminAuth: true }
    },
    ...companyRoutes.filter(route => route.path).map(route => ({
      path: '/' + route.path, name: 'legacy-' + String(route.name),
      component: () => import('@/views/NoTenantView.vue'), meta: { legacy: true }
    })),
    { path: '/:companyUrlName', meta: { isCompany: true }, children: [
      ...companyRoutes,
      { path: ':pathMatch(.*)*', name: 'company-not-found', component: () => import('@/views/NoTenantView.vue') }
    ] }
  ]
})

router.beforeEach(async to => {
  if (to.meta.isAdmin) {
    if (to.meta.requiresAdminAuth && !sessionStorage.getItem('salesRepApp_adminToken')) return { name: 'admin-login' }
    return true
  }
  const query = { ...to.query }
  delete query.t
  const requestedName = typeof to.params.companyUrlName === 'string' ? to.params.companyUrlName : undefined
  const tenantId = typeof to.query.t === 'string' ? to.query.t : undefined
  try {
    if (requestedName) {
      const current = getConfig()
      if (current && current.urlName.toLowerCase() !== requestedName.replace(/\s/g, '').toLowerCase()) {
        window.location.assign(to.fullPath)
        return false
      }
      if (!current) await loadServerConfig({ urlName: requestedName })
      const suffixIndex = to.path.indexOf('/', 1)
      const canonicalPath = companyPath(suffixIndex < 0 ? '' : to.path.slice(suffixIndex))
      if (to.path !== canonicalPath || Object.prototype.hasOwnProperty.call(to.query, 't')) {
        return { path: canonicalPath, query, hash: to.hash, replace: true }
      }
    } else if (tenantId || (isDevelopment() && to.name !== 'no-tenant')) {
      const config = await loadServerConfig(tenantId ? { t: tenantId } : undefined)
      if (config) {
        return { path: companyPath(to.meta.legacy ? to.path : '', config.urlName), query, hash: to.hash, replace: true }
      }
    }
    if (to.meta.legacy) return { name: 'welcome', query: { next: to.fullPath }, replace: true }
    if (!to.meta.isCompany) return true
    if (to.name === 'company-not-found') {
      companyAccessError.value = 'That page could not be found. Use your company link to sign in again.'
      return { name: 'no-tenant' }
    }
    const auth = useAuthStore()
    auth.restoreSession()
    if (to.name === 'company-home') return { path: companyPath(isAuthenticated() ? '/dashboard' : '/login'), query, hash: to.hash, replace: true }
    if (to.meta.requiresAuth && !isAuthenticated()) {
      if (isDevelopment() && await auth.tryDevAutoLogin()) return true
      return { path: companyPath('/login'), query: { ...query, returnTo: to.fullPath }, replace: true }
    }
    if (to.name === 'login' && isAuthenticated()) return safeCompanyReturn(to.query.returnTo) || companyPath('/dashboard')
    return true
  } catch (error) {
    companyAccessError.value = error instanceof Error ? error.message : 'Company access is temporarily unavailable. Please retry.'
    return { name: 'no-tenant', replace: true }
  }
})

export default router
